import React, { useState, useEffect, useCallback } from 'react';
import {
  getLeaveTypes, createLeaveType, updateLeaveType, deleteLeaveType,
  getLeaveConfig, updateLeaveConfig, addLeaveApprover, removeLeaveApprover,
  getEmployees, getTeams,
} from '../../api';

const RESET_CYCLES = [
  { v: 'annually', l: 'Annually' },
  { v: 'monthly', l: 'Monthly' },
  { v: 'never', l: 'Never' },
];
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 14, background: 'var(--glass-2)', color: 'var(--text)', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '14px 0 6px' };
const cap = s => s ? s[0].toUpperCase() + s.slice(1) : s;

// ── Add / Edit Leave Type modal ──────────────────────────────────────────────
function LeaveTypeModal({ initial, employees, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    allowed_days: initial?.allowed_days == null ? '' : String(initial.allowed_days),
    reset_cycle: initial?.reset_cycle || 'annually',
    carry_forward: !!initial?.carry_forward,
    category: initial?.category || 'paid',
    assign_all: initial ? !!initial.assign_all : true,
    members: initial?.members || [],
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = {
        name: form.name.trim(),
        allowed_days: form.allowed_days === '' ? null : parseInt(form.allowed_days),
        reset_cycle: form.reset_cycle, carry_forward: form.carry_forward,
        category: form.category, assign_all: form.assign_all,
        members: form.assign_all ? [] : form.members,
      };
      if (editing) await updateLeaveType(initial.id, payload);
      else await createLeaveType(payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editing ? 'Edit Leave Type' : 'Add Leave Type'}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>

        <label style={lbl}>Name of the Leave Type <span style={{ color: '#dc2626' }}>*</span></label>
        <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Casual Leave" autoFocus />

        <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: 14, marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Leave Balance Settings</div>
          <label style={lbl}>Total Balance</label>
          <div style={{ display: 'flex' }}>
            <input style={{ ...inp, borderTopRightRadius: 0, borderBottomRightRadius: 0 }} type="number" min="0" value={form.allowed_days} onChange={e => set('allowed_days', e.target.value)} placeholder="Ex: 5" />
            <span style={{ padding: '9px 14px', border: '1px solid var(--glass-border)', borderLeft: 'none', borderTopRightRadius: 8, borderBottomRightRadius: 8, background: 'var(--hairline)', color: 'var(--muted)', fontSize: 14 }}>Days</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 5 }}>If you don't enter any value, unlimited leaves will be applied.</div>

          <label style={lbl}>Reset Leave Balance</label>
          <select style={inp} value={form.reset_cycle} onChange={e => set('reset_cycle', e.target.value)}>
            {RESET_CYCLES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 0', fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.carry_forward} onChange={e => set('carry_forward', e.target.checked)} disabled={form.reset_cycle === 'never'} />
            Enable leave carry forward
          </label>

          <label style={lbl}>Choose Leave Category</label>
          <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>🔒 Assign this leave type to all members</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Turn off to assign specific members.</div>
          </div>
          <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
            <input type="checkbox" checked={form.assign_all} onChange={e => set('assign_all', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: form.assign_all ? '#22c55e' : 'var(--hairline)', borderRadius: 24, transition: '.2s' }}>
              <span style={{ position: 'absolute', height: 18, width: 18, left: form.assign_all ? 23 : 3, top: 3, background: '#fff', borderRadius: '50%', transition: '.2s' }} />
            </span>
          </label>
        </div>

        {!form.assign_all && (
          <div style={{ marginTop: 12, maxHeight: 160, overflowY: 'auto', border: '1px solid var(--hairline)', borderRadius: 8, padding: 8 }}>
            {employees.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, padding: 8 }}>No employees.</div>}
            {employees.map(e => (
              <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.members.includes(e.id)}
                  onChange={ev => set('members', ev.target.checked ? [...form.members, e.id] : form.members.filter(x => x !== e.id))} />
                {e.name}
              </label>
            ))}
          </div>
        )}

        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{err}</div>}
        <button onClick={save} disabled={busy} style={{ width: '100%', marginTop: 18, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Saving…' : editing ? 'Save Changes' : 'Create Leave Type'}
        </button>
      </div>
    </div>
  );
}

const Card = ({ title, right, children }) => (
  <div className="card" style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{title}</div>
      {right}
    </div>
    {children}
  </div>
);

export default function LeaveSettings() {
  const [tab, setTab] = useState('active');
  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [config, setConfig] = useState(null);
  const [modal, setModal] = useState(null); // null | {} (new) | type (edit)
  const [msg, setMsg] = useState('');
  const [newApprover, setNewApprover] = useState({ employee_id: '', team_id: '' });

  const load = useCallback(async () => {
    try {
      const [a, ar, emps, tm, cfg] = await Promise.all([
        getLeaveTypes(false), getLeaveTypes(true), getEmployees(), getTeams().catch(() => []), getLeaveConfig(),
      ]);
      setActive(a); setArchived(ar); setEmployees(emps); setTeams(tm); setConfig(cfg);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const archive = async (t, val) => { await updateLeaveType(t.id, { archived: val }); flash(val ? 'Archived.' : 'Restored.'); load(); };
  const remove = async (t) => { if (!window.confirm(`Delete "${t.name}"? This removes the leave type (existing requests keep their record).`)) return; await deleteLeaveType(t.id); flash('Deleted.'); load(); };

  const saveConfig = async (patch) => { const c = await updateLeaveConfig(patch); setConfig(cfg => ({ ...cfg, ...c })); flash('Saved.'); };
  const addApprover = async () => {
    if (!newApprover.employee_id) return;
    await addLeaveApprover({ employee_id: newApprover.employee_id, team_id: newApprover.team_id || null });
    setNewApprover({ employee_id: '', team_id: '' }); flash('Approver added.'); load();
  };
  const delApprover = async (id) => { await removeLeaveApprover(id); load(); };

  if (!config) return <div style={{ padding: 40, color: 'var(--muted)' }}>Loading leave settings…</div>;
  const rows = tab === 'active' ? active : archived;

  return (
    <div style={{ maxWidth: 1000 }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: 4 }}>Leave Settings</h2>
      {msg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: 8, padding: '8px 12px', margin: '10px 0', fontSize: 13 }}>{msg}</div>}

      {/* Leave Types */}
      <Card title="Leave Type Settings"
        right={<button onClick={() => setModal({})} style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>+ Add New Leave Type</button>}>
        <div style={{ display: 'flex', gap: 18, borderBottom: '1px solid var(--hairline)', marginBottom: 12 }}>
          {[['active', `Active Leave Types (${active.length})`], ['archived', `Archived Leaves (${archived.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 2px', fontSize: 14, fontWeight: 600, color: tab === k ? '#2563eb' : 'var(--muted)', borderBottom: tab === k ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1 }}>{l}</button>
          ))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                {['Leave Type', 'Allowed Days', 'Leave Cycle', 'Carry Forward', 'Paid/Unpaid', 'Visibility', ''].map(h =>
                  <th key={h} style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid var(--hairline)' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>No {tab} leave types.</td></tr>}
              {rows.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                  <td style={{ padding: '10px', fontWeight: 600 }}>{t.name}</td>
                  <td style={{ padding: '10px' }}>{t.allowed_days == null ? 'Unlimited' : `${t.allowed_days} days`}</td>
                  <td style={{ padding: '10px' }}>{cap(t.reset_cycle)}</td>
                  <td style={{ padding: '10px' }}>{t.carry_forward ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '10px' }}>{t.category === 'unpaid' ? 'Unpaid Leave' : 'Paid Leave'}</td>
                  <td style={{ padding: '10px' }}>{t.assign_all ? 'All Employees' : `${(t.members || []).length} member(s)`}</td>
                  <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                    <button title="Edit" onClick={() => setModal(t)} style={iconBtn}>✏️</button>
                    <button title={t.archived ? 'Restore' : 'Archive'} onClick={() => archive(t, t.archived ? 0 : 1)} style={iconBtn}>{t.archived ? '↩️' : '🗄️'}</button>
                    <button title="Delete" onClick={() => remove(t)} style={iconBtn}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Approvers */}
      <Card title="Leave Approvers">
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
          Admins &amp; managers can always approve. Add specific people (optionally scoped to a team) as additional approvers.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {(config.approvers || []).length === 0 && <span style={{ color: 'var(--muted)', fontSize: 13 }}>No extra approvers configured.</span>}
          {(config.approvers || []).map(a => (
            <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--glass-1, #ecfdf5)', border: '1px solid var(--hairline)', borderRadius: 999, padding: '5px 12px', fontSize: 13 }}>
              👤 {a.employee_name || 'Unknown'} · {a.team_name || 'All Teams'}
              <button onClick={() => delApprover(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 15 }}>×</button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ ...lbl, margin: '0 0 6px' }}>Person</label>
            <select style={inp} value={newApprover.employee_id} onChange={e => setNewApprover(a => ({ ...a, employee_id: e.target.value }))}>
              <option value="">Select a person…</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ ...lbl, margin: '0 0 6px' }}>Scope</label>
            <select style={inp} value={newApprover.team_id} onChange={e => setNewApprover(a => ({ ...a, team_id: e.target.value }))}>
              <option value="">All Teams (Global)</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <button onClick={addApprover} disabled={!newApprover.employee_id} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer' }}>+ Add</button>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
        {/* Annual cycle */}
        <Card title="Annual Cycle Year Configuration">
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>Choose the annual cycle. Leave balances reset based on this.</div>
          <select style={inp} value={config.annual_cycle} onChange={e => saveConfig({ annual_cycle: e.target.value })}>
            <option value="calendar">Calendar Year (1st January – 31st December)</option>
            <option value="financial">Financial Year (1st April – 31st March)</option>
          </select>
        </Card>

        {/* Short leave */}
        <Card title="Short Leave">
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>Allow partial-day (short) leave requests.</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={config.short_enabled} onChange={e => saveConfig({ short_enabled: e.target.checked })} />
            {config.short_enabled ? 'Short leave enabled' : 'Enable short leave'}
          </label>
        </Card>

        {/* Permissions */}
        <Card title="Permissions">
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={config.allow_past} onChange={e => saveConfig({ allow_past: e.target.checked })} />
            Enable apply leave for past days
          </label>
        </Card>
      </div>

      {modal && <LeaveTypeModal initial={modal.id ? modal : null} employees={employees} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 6px' };
